import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { X } from 'lucide-react';

export const CZ_COLORS = [
  { bg: 'rgba(219,234,254,0.28)', border: 'rgba(147,197,253,0.7)', text: '#1e40af', hex: '#eff6ff', name: 'Mavi' },
  { bg: 'rgba(220,252,231,0.28)', border: 'rgba(134,239,172,0.7)', text: '#15803d', hex: '#f0fdf4', name: 'Yeşil' },
  { bg: 'rgba(254,249,195,0.28)', border: 'rgba(253,224,71,0.7)',  text: '#92400e', hex: '#fefce8', name: 'Sarı' },
  { bg: 'rgba(243,232,255,0.28)', border: 'rgba(216,180,254,0.7)', text: '#7e22ce', hex: '#faf5ff', name: 'Mor' },
  { bg: 'rgba(255,237,213,0.28)', border: 'rgba(253,186,116,0.7)', text: '#c2410c', hex: '#fff7ed', name: 'Turuncu' },
  { bg: 'rgba(252,231,243,0.28)', border: 'rgba(249,168,212,0.7)', text: '#be185d', hex: '#fdf2f8', name: 'Pembe' },
  { bg: 'rgba(204,251,241,0.28)', border: 'rgba(94,234,212,0.7)',  text: '#0f766e', hex: '#f0fdfa', name: 'Turkuaz' },
  { bg: 'rgba(254,226,226,0.28)', border: 'rgba(252,165,165,0.7)', text: '#b91c1c', hex: '#fff1f2', name: 'Kırmızı' },
];

/**
 * Modal for creating or editing a ceiling height zone.
 * Props:
 *   show, onClose, onSave(ceilingHeight, maxStandHeight, colorIndex)
 *   initial: { ceiling_height, max_stand_height, color_index }   (for edit)
 *   pendingCells: number (how many cells are being assigned)
 *   existingZones: array of existing zones (to detect same-height zones)
 */
export function CeilingZoneModal({ show, onClose, onSave, initial, pendingCells, existingZones = [] }) {
  const [ceilH, setCeilH]   = useState('');
  const [maxH,  setMaxH]    = useState('');
  const [colorIdx, setColorIdx] = useState(0);

  // Find a matching existing zone when heights change
  const matchingZone = (() => {
    const ch = parseFloat(ceilH);
    const mh = parseFloat(maxH);
    if (!ch || !mh) return null;
    return existingZones.find(z => z.ceiling_height === ch && z.max_stand_height === mh) ?? null;
  })();
  const colorLocked = matchingZone !== null;

  useEffect(() => {
    if (show) {
      setCeilH(initial?.ceiling_height != null ? String(initial.ceiling_height) : '');
      setMaxH( initial?.max_stand_height != null ? String(initial.max_stand_height) : '');
      setColorIdx(initial?.color_index ?? 0);
    }
  }, [show, initial]);

  // Auto-assign color when a matching zone is found
  useEffect(() => {
    if (matchingZone) {
      setColorIdx(matchingZone.color_index);
    }
  // eslint-disable-next-line
  }, [ceilH, maxH]);

  if (!show) return null;

  const handleSave = () => {
    const ch = parseFloat(ceilH);
    const mh = parseFloat(maxH);
    if (!ch || ch <= 0) { alert('Geçerli bir tavan yüksekliği girin'); return; }
    if (!mh || mh <= 0) { alert('Geçerli bir maks. stand yüksekliği girin'); return; }
    if (mh > ch) { alert('Stand yüksekliği tavan yüksekliğinden fazla olamaz'); return; }
    onSave(ch, mh, colorIdx);
  };

  const color = CZ_COLORS[colorIdx];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="ceiling-zone-modal">
      <div className="bg-white rounded-xl shadow-2xl w-96 border border-gray-200">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="text-base font-semibold text-gray-800">
            {initial?.id ? 'Tavan Bölgesi Düzenle' : 'Tavan Bölgesi Ekle'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {pendingCells > 0 && (
            <p className="text-xs text-gray-500 bg-gray-50 px-3 py-2 rounded-lg border">
              <span className="font-medium text-gray-700">{pendingCells}</span> hücre seçildi
              ({pendingCells}m²)
            </p>
          )}

          <div className="space-y-1">
            <Label htmlFor="ceil-h" className="text-sm font-medium">Tavan Yüksekliği (m)</Label>
            <Input
              id="ceil-h"
              type="number"
              step="0.1"
              min="1"
              max="50"
              value={ceilH}
              onChange={e => setCeilH(e.target.value)}
              placeholder="örn: 6.0"
              data-testid="ceiling-height-input"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="max-h" className="text-sm font-medium">Maks. Stand Yüksekliği (m)</Label>
            <Input
              id="max-h"
              type="number"
              step="0.1"
              min="0.5"
              max="50"
              value={maxH}
              onChange={e => setMaxH(e.target.value)}
              placeholder="örn: 4.0"
              data-testid="max-stand-height-input"
            />
          </div>

          {/* Color picker */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Bölge Rengi</Label>
              {colorLocked && (
                <span className="text-xs text-amber-600 font-medium bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
                  Mevcut bölgeyle aynı renk
                </span>
              )}
            </div>
            <div className={`flex flex-wrap gap-2 ${colorLocked ? 'opacity-40 pointer-events-none select-none' : ''}`}>
              {CZ_COLORS.map((c, i) => (
                <button
                  key={i}
                  onClick={() => !colorLocked && setColorIdx(i)}
                  title={colorLocked ? 'Aynı yüksekliğe sahip mevcut bölgenin rengi otomatik uygulandı' : c.name}
                  disabled={colorLocked}
                  className={`w-7 h-7 rounded-full border-2 transition-all ${colorIdx === i ? 'scale-125 shadow-md' : 'hover:scale-110'}`}
                  style={{ backgroundColor: c.hex, borderColor: colorIdx === i ? c.border : 'transparent' }}
                  data-testid={`zone-color-${i}`}
                />
              ))}
            </div>
          </div>

          {/* Preview */}
          {ceilH && maxH && (
            <div
              className="px-3 py-2 rounded-lg text-xs font-medium border"
              style={{ backgroundColor: color.hex, borderColor: color.border, color: color.text }}
            >
              Önizleme: Tavan {ceilH}m · Maks. Stand {maxH}m
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 pb-4">
          <Button variant="outline" size="sm" onClick={onClose} data-testid="cz-modal-cancel">İptal</Button>
          <Button size="sm" onClick={handleSave} data-testid="cz-modal-save">
            {initial?.id ? 'Güncelle' : 'Bölge Ekle'}
          </Button>
        </div>
      </div>
    </div>
  );
}
